/**
 * A failed action, rendered as what it actually is.
 *
 * Most failures are errors. Some are prices. The API distinguishes them — a
 * plan boundary comes back 402 carrying the feature or the limit, the cap, the
 * plan you are on and where to upgrade — and until now every screen threw all
 * of that away and printed the sentence, so "You have reached the limit for
 * your plan" arrived without saying which limit, what the cap was, or what
 * would lift it. A dead end where a purchasable one belonged.
 *
 * Rendering-time gates already did this properly: a screen that can ask
 * `entitlements.has(...)` before it draws shows an upgrade prompt. This is the
 * same answer for the boundaries only the server can find — a seat number, a
 * partial refund, the sixth storage — which are exactly the ones a person hits
 * while trying to do the thing rather than while looking at a menu.
 *
 * A 403 gets the same treatment for the same reason. The server names the
 * permission that was missing, and "not allowed" without it sends somebody to
 * ask a manager a question neither of them can answer. Which permission is the
 * whole of what they need to know: who to ask, and what to ask for.
 *
 * Narrowed structurally rather than by importing the error classes, because
 * this package does not depend on the API client and should not start: a 402
 * with a plan in the body, or a 403 with a permission in it, is recognisable
 * without knowing the class.
 */

import type { ReactNode } from 'react';

import { ErrorNotice } from './primitives';
import { UpgradePrompt } from './upgrade';

interface UpgradeBody {
  cap?: unknown;
  current_plan?: unknown;
  required_plan?: unknown;
}

function bodyWithStatus(error: unknown, status: number): Record<string, unknown> | null {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as { status?: unknown; body?: unknown };
  if (candidate.status !== status) return null;
  if (typeof candidate.body !== 'object' || candidate.body === null) return null;
  return candidate.body as Record<string, unknown>;
}

function upgradeBody(error: unknown): UpgradeBody | null {
  return bodyWithStatus(error, 402) as UpgradeBody | null;
}

/** The permission a 403 says was missing, or null if it did not say. */
function deniedPermission(error: unknown): string | null {
  const body = bodyWithStatus(error, 403);
  const permission = body?.permission;
  return typeof permission === 'string' && permission !== '' ? permission : null;
}

export function ActionError({
  error,
  action,
  title,
  onUpgrade,
  describePermission,
}: {
  error: unknown;
  /**
   * What they were trying to do, in their words — "Another storage", "A
   * partial refund". Only the call site knows this, and it is what makes the
   * prompt read like an answer rather than a status code.
   */
  action: string;
  /** Heading for the ordinary-failure case. */
  title?: string | undefined;
  onUpgrade?: (() => void) | undefined;
  /**
   * How to word a permission key for a person. Optional because this package
   * cannot know the product's vocabulary; without it the key is shown as-is,
   * which is still more useful than no key at all.
   */
  describePermission?: ((key: string) => string) | undefined;
}): ReactNode {
  if (!error) return null;

  const body = upgradeBody(error);
  if (body) {
    return (
      <UpgradePrompt
        action={action}
        {...(typeof body.cap === 'number' ? { cap: body.cap } : {})}
        {...(typeof body.current_plan === 'string' ? { currentPlan: body.current_plan } : {})}
        {...(typeof body.required_plan === 'string'
          ? { requiredPlan: body.required_plan }
          : {})}
        {...(onUpgrade ? { onUpgrade } : {})}
      />
    );
  }

  const missing = deniedPermission(error);
  if (missing) {
    // `*` is every permission. Only an Owner holds it, so the only useful
    // thing to say is who can do this, not what to add to a role.
    const message =
      missing === '*'
        ? `${action} needs every permission there is, which only an Owner holds.`
        : `${action} needs “${describePermission ? describePermission(missing) : missing}”, which your role does not include. Somebody who holds it can do this, or add it to your role.`;
    return <ErrorNotice title={title ?? 'Not allowed'} message={message} />;
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Something went wrong';

  return <ErrorNotice {...(title ? { title } : {})} message={message} />;
}
